/**
 * Simple client-side router for handling direct URLs
 * This allows for static page routing like /privacy, /contactus, /terms
 */

export type Route = 
  | "landing" 
  | "image-to-video" 
  | "thumbnail" 
  | "banner" 
  | "video-editor" 
  | "auto-captions" 
  | "shorts-clipper" 
  | "video-composer-v3"
  | "video-stack-composer"
  | "gif-creator"
  | "blog"
  | "blog-post"
  | "whats-new"
  | "how-it-works" 
  | "tutorials" 
  | "privacy-policy"
  | "contact-us"
  | "terms-and-conditions"
  | "sitemap"
  | "admin";

export const getRouteFromPath = (pathname: string): Route => {
  const path = pathname.toLowerCase().replace(/^\/+|\/+$/g, ''); // Remove leading/trailing slashes
  
  switch (path) {
    case '':
    case 'home':
      return 'landing';
    case 'privacy':
    case 'privacy-policy':
      return 'privacy-policy';
    case 'contact':
    case 'contactus':
    case 'contact-us':
      return 'contact-us';
    case 'terms':
    case 'terms-and-conditions':
      return 'terms-and-conditions';
    case 'about':
    case 'how-it-works':
      return 'how-it-works';
    case 'tutorials':
      return 'tutorials';
    case 'auto-captions':
    case 'captions':
    case 'caption-creator':
      return 'auto-captions';
    case 'video-editor':
    case 'editor':
      return 'video-editor';
    case 'video-composer':
    case 'composer':
    case 'advanced-composer':
      return 'video-composer-v3';
    case 'all-in-one':
    case 'all-in-one-editor':
    case 'video-composer-v3':
      return 'video-composer-v3';
    case 'video-stack-composer':
    case 'simple-composer':
    case 'simple-composer-2':
    case 'simple-video-editor':
      return 'video-stack-composer';
    case 'shorts-clipper':
    case 'clipper':
      return 'shorts-clipper';
    case 'thumbnail':
    case 'thumbnail-creator':
      return 'thumbnail';
    case 'banner':
    case 'banner-creator':
      return 'banner';
    case 'image-to-video':
    case 'image-video':
      return 'image-to-video';
    case 'gif-creator':
    case 'gif':
      return process.env.NODE_ENV === 'development' ? 'gif-creator' : 'landing';
    case 'blog':
      return 'blog';
    case 'whats-new':
    case 'whatsnew':
    case 'changelog':
      return 'whats-new';
    case 'sitemap':
    case 'sitemap.xml':
      return 'sitemap';
    default:
      if (path.startsWith('blog/') && path.split('/').length === 2) {
        return 'blog-post';
      }
      if (path.startsWith('admin')) {
        return 'landing'; // Let server handle it
      }
      return 'landing';
  }
};

export const getPathFromRoute = (route: Route): string => {
  switch (route) {
    case 'landing':
      return '/';
    case 'privacy-policy':
      return '/privacy';
    case 'contact-us':
      return '/contact';
    case 'terms-and-conditions':
      return '/terms';
    case 'how-it-works':
      return '/about';
    case 'tutorials':
      return '/tutorials';
    case 'auto-captions':
      return '/auto-captions';
    case 'video-editor':
      return '/video-editor';
    case 'video-composer-v3':
      return '/video-composer';
    case 'video-stack-composer':
      return '/video-stack-composer';
    case 'shorts-clipper':
      return '/shorts-clipper';
    case 'thumbnail':
      return '/thumbnail';
    case 'banner':
      return '/banner';
    case 'image-to-video':
      return '/image-to-video';
    case 'gif-creator':
      return '/gif-creator';
    case 'blog':
      return '/blog';
    case 'blog-post':
      return '/blog'; // Will be handled by component with slug
    case 'whats-new':
      return '/whats-new';
    case 'sitemap':
      return '/sitemap';
    case 'admin':
      return '/admin';
    default:
      return '/';
  }
};

export const updateUrl = (route: Route) => {
  const path = getPathFromRoute(route);
  if (path !== window.location.pathname) {
    window.history.pushState({}, '', path);
    // Always scroll to top on navigation
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    } catch {
      window.scrollTo(0, 0);
    }
    // Notify listeners of route change (pushState doesn't emit popstate)
    try {
      window.dispatchEvent(new Event('routechange'));
    } catch {}
  }
};

export const initializeRouter = (onRouteChange: (route: Route) => void) => {
  // Handle initial route
  const initialRoute = getRouteFromPath(window.location.pathname);
  onRouteChange(initialRoute);
  // Ensure initial view starts at top
  try {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  } catch {
    window.scrollTo(0, 0);
  }

  // Handle browser back/forward
  window.addEventListener('popstate', () => {
    const route = getRouteFromPath(window.location.pathname);
    onRouteChange(route);
    // Scroll to top on browser navigation
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    } catch {
      window.scrollTo(0, 0);
    }
  });
};
